import { LogoutIcon } from "@root/assets/icons";
import { LANGUAGE, ROUTE_NAMES } from "@root/constants";
import useCustomTheme from "@root/hooks/useCustomTheme";
import useMediaQ from "@root/hooks/useMediaQ";
import {
  getLoggedInUser,
  selectChatsdownStatus,
  selectProfile,
  selectThreads,
} from "@root/store/selectors/entities";
import {
  updateChatPID,
  updateChatsDropdownStatus,
  updateCurrentThreadId,
  updateRiskCheckerSelectedFiles,
  updateRiskCheckerSelectedFilesURLs,
  updateThreadHistory,
  updateThreadList,
  updateThreads,
} from "@root/store/slices/entities";
import {
  deleteAccount,
  getThreads,
  getUserProfile,
  logout,
} from "@root/store/thunks";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useLanguage from "./useLanguage";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { BalanceIcon } from "@root/assets/icons/balance";
import { ReactComponent as DashboardIcon } from "@root/assets/icons/dashboard-icon.svg";
import { MaterialIcon } from "@root/assets/icons/materialicon";
import { FolderIcon } from "@root/assets/icons/foldericon";
import PermitIcon from "@root/assets/icons/permit-icon";

function useSidebar() {
  const isLoggedInUser: any = useSelector(getLoggedInUser);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [updatePasswordOpen, setUpdatePasswordOpen] = useState(false);
  const [isOpen, setisOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<{
    [key: string]: boolean;
  }>({});
  const threads: any = useSelector(selectThreads);
  const chatsDropdownStatus: boolean = useSelector(selectChatsdownStatus);
  const { lg } = useMediaQ();
  const { isDarkTheme } = useCustomTheme();
  const { id } = useParams();
  const { pathname } = useLocation();
  const { handleUpdateLanguage, currentLanguage } = useLanguage();
  const {
    setCurrentMessageResponse,
    getInitialThreads: getInitialThreadsSidebar,
  } = useMainChatContext();

  const [logoutPopup, setLogoutPopup] = useState(false);
  const [deleteAccountPopup, setDeleteAccountPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const profileData = useSelector(selectProfile);
  const { t } = useTranslation("common");

  const getInitialThreads = () => {
    dispatch(
      getThreads({
        onSuccess: (res: any) => {
          dispatch(updateThreads(res));
        },
      })
    );
  };

  const getInitialData = () => {
    dispatch(getUserProfile({}));
  };

  useEffect(() => {
    getInitialData();
    getInitialThreads();
    getInitialThreadsSidebar();
  }, []);

  const sidebar = useMemo(() => {
    return [
      {
        key: "dashboard",
        text: t("sidebar.dashboard"),
        active: false,
        iconComponent: DashboardIcon,
        onClick: () => navigate(ROUTE_NAMES.DASHBOARD),
        routes: [ROUTE_NAMES.DASHBOARD],
      },
      // 👉 Section heading: Legal
      // {
      //   key: "heading-items",
      //   text: "ITEMS",
      //   isHeading: true,
      //   section: "ITEMS",
      // },

      // {
      //   key: "projectDocumentChatbot",
      //   text: t("sidebar.projectDocumentChatbot"),
      //   active: false,
      //   iconComponent: FolderIcon,
      //   onClick: () => navigate(ROUTE_NAMES.DOCUMENT_INTEGRATION_CHATBOT),
      //   routes: [ROUTE_NAMES.DOCUMENT_INTEGRATION_CHATBOT],
      //   section: "llm",
      // },
      {
        key: "legalChatbot",
        text: t("sidebar.legal"),
        active: false,
        iconComponent: BalanceIcon,
        onClick: () => navigate(ROUTE_NAMES.LEGAL_CHATBOT),
        routes: [
          ROUTE_NAMES.LEGAL_CHATBOT,
          ROUTE_NAMES.KSCE_CHAT_SCREEN,
          ROUTE_NAMES.KCS,
          ROUTE_NAMES.KCS_CHAT,
        ],
        section: "ITEMS",
      },
      {
        key: "projectDocumentChatbot",
        text: t("sidebar.project"),
        active: false,
        iconComponent: FolderIcon,
        onClick: () => navigate(ROUTE_NAMES.DOCUMENT_INTEGRATION_CHATBOT),
        routes: [
          ROUTE_NAMES.DOCUMENT_INTEGRATION_CHATBOT,
          ROUTE_NAMES.DOCUMENT_CHAT,
          ROUTE_NAMES.DRAWING_ANALYSIS_DASHBOARD,
          ROUTE_NAMES.DRAWING_ANALYSIS_CHAT,
          ROUTE_NAMES.CHECKLIST,
          ROUTE_NAMES.CHECKLIST_CHAT,
        ],
        section: "ITEMS",
      },
      {
        key: "permitReview",
        text: t("sidebar.permit"),
        active: false,
        iconComponent: PermitIcon,
        onClick: () => navigate(ROUTE_NAMES.PERMIT_REVIEW),
        routes: [
          ROUTE_NAMES.PERMIT_REVIEW,
          "/permit-review",
          "/legalreview",
          ROUTE_NAMES.LEGAL_REVIEW,
          ROUTE_NAMES.EXTRACTION_REVIEW,
        ],
        section: "ITEMS",
      },
      //{
      //   key: "legalReview",
      //   text: t("sidebar.legalReview"),
      //   active: false,
      //   iconComponent: BalanceIcon,
      //   onClick: () => navigate(ROUTE_NAMES.LEGAL_REVIEW),
      //   routes: [ROUTE_NAMES.LEGAL_REVIEW],
      //   section: "permit",
      // },
      // {
      //   key: "permitReview",
      //   text: t("sidebar.permitReview"),
      //   active: false,
      //   iconComponent: CalendarIcon,
      //   onClick: () => navigate(ROUTE_NAMES.PERMIT_REVIEW),
      //   routes: [ROUTE_NAMES.PERMIT_REVIEW],
      //   section: "permit",
      // },
      // // 👉 Section heading: Material Comparison
      // {
      //   key: "heading-material-comparison",
      //   text: t("sidebar.materialComparisonHeading"),
      //   isHeading: true,
      //   section: "material-comparison",
      // },
      {
        key: "materialComparisonNested",
        text: t("sidebar.materialComparison"),
        active: false,
        iconComponent: MaterialIcon,
        onClick: () => navigate(ROUTE_NAMES.MATERIAL_CAMPARISON),
        routes: [ROUTE_NAMES.MATERIAL_CAMPARISON],
        section: "ITEMS",
      },
      // {
      //   key: "drawingAnalysisChatbot",
      //   text: t("sidebar.drawingAnalysisChatbot"),
      //   active: false,
      //   iconComponent: DrawingIcon,
      //   onClick: () => navigate(ROUTE_NAMES.DRAWING_ANALYSIS),
      //   routes: [ROUTE_NAMES.DRAWING_ANALYSIS],
      //   section: "llm",
      // },
      // {
      //   key: "kcsChatbot",
      //   text: t("sidebar.kcsChatbot"),
      //   active: false,
      //   iconComponent: MessageIcon,
      //   onClick: () => navigate(ROUTE_NAMES.KCS),
      //   routes: [ROUTE_NAMES.KCS],
      //   section: "llm",
      // },
      // Section heading: Agent
      // {
      //   key: "heading-agent",
      //   text: "Agent",
      //   isHeading: true,
      //   section: "agent",
      // },
      // {
      //   key: "legalReview",
      //   text: t("sidebar.legalReview"),
      //   active: false,
      //   iconComponent: BalanceIcon,
      //   onClick: () => navigate(ROUTE_NAMES.LEGAL_REVIEW),
      //   routes: [ROUTE_NAMES.LEGAL_REVIEW],
      //   section: "agent",
      // },
      // {
      //   key: "permitReview",
      //   text: t("sidebar.permitReview"),
      //   active: false,
      //   iconComponent: CalendarIcon,
      //   onClick: () => navigate(ROUTE_NAMES.PERMIT_REVIEW),
      //   routes: [ROUTE_NAMES.PERMIT_REVIEW],
      //   section: "agent",
      // },
      // {
      //   key: "checklist",
      //   text: t("sidebar.checklist"),
      //   active: false,
      //   iconComponent: ChecklistIcon,
      //   onClick: () => navigate(ROUTE_NAMES.CHECKLIST),
      //   routes: [ROUTE_NAMES.CHECKLIST],
      //   section: "agent",
      // },
      // {
      //   key: "materialComparison",
      //   text: t("sidebar.materialComparison"),
      //   active: false,
      //   iconComponent: MaterialIcon,
      //   onClick: () => navigate(ROUTE_NAMES.MATERIAL_CAMPARISON),
      //   routes: [ROUTE_NAMES.MATERIAL_CAMPARISON],
      //   section: "agent",
      // },
    ];
  }, [isLoggedInUser?.roleId, navigate, t]);

  const menuItemsList = [
    {
      title: t("sidebar.updatePassword"),
      onClick: () => setUpdatePasswordOpen(true),
    },
    {
      title: t("sideMenu.Logout"),
      icon: LogoutIcon({ rotate: "180deg" }),
      onClick: () => setLogoutPopup(true),
      routes: [],
    },
  ];

  const languages = [
    {
      title: "ENG",
      value: "en",
      onClick: () => handleUpdateLanguage(LANGUAGE.ENGLISH),
      isActive: currentLanguage === LANGUAGE.ENGLISH,
    },
    {
      title: "KOR",
      value: "ko",
      onClick: () => handleUpdateLanguage(LANGUAGE.KOREAN),
      isActive: currentLanguage === LANGUAGE.KOREAN,
    },
  ];

  const clickOnAdd = () => {
    navigate(ROUTE_NAMES.LEGAL_CHATBOT);
    setCurrentMessageResponse(null);
    dispatch(updateThreadList([] as any));
    dispatch(updateRiskCheckerSelectedFilesURLs([] as any));
    dispatch(updateRiskCheckerSelectedFiles([] as any));
    dispatch(updateChatPID(null as any));
    dispatch(updateCurrentThreadId(null as any));
    dispatch(updateThreadHistory([] as any));
  };

  const clickOnCollapseArrow = (e: any) => {
    e.stopPropagation();
    dispatch(updateChatsDropdownStatus(!chatsDropdownStatus));
  };
  const clickOnCollapse = () => {
    dispatch(updateChatsDropdownStatus(false));
  };
  const logoutHandler = () => {
    dispatch(logout({}));
    navigate(ROUTE_NAMES.LOGIN);
  };

  const deleteUserAccount = () => {
    setLoading(true);
    dispatch(
      deleteAccount({
        onSuccess: () => {
          setLoading(false);
          logoutHandler();
        },
      })
    );
  };

  useEffect(() => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [chatsDropdownStatus, pathname, currentLanguage]);

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return {
    isOpen,
    isDarkTheme,
    lg,
    navigate,
    dispatch,
    sidebar,
    pathname,
    chatsDropdownStatus,
    languages,
    menuItemsList,
    chatId: id,
    clickOnAdd,
    clickOnCollapseArrow,
    clickOnCollapse,
    threads,
    profileData,
    logoutPopup,
    setLogoutPopup,
    deleteAccountPopup,
    setDeleteAccountPopup,
    logoutHandler,
    deleteUserAccount,
    loading,
    setLoading,
    updatePasswordOpen,
    setUpdatePasswordOpen,
    t,
    isMenuOpen,
    setIsMenuOpen,
    collapsedSections,
    toggleSection,
  };
}

export default useSidebar;
